import {
  checkboxFilter,
  column,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  Icon,
  multiFilter,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  Tooltip,
  useGridTableLayoutState,
} from "@homebound/beam";
import { capitalCase } from "change-case";
import { useCallback, useMemo } from "react";
import { chipCell, dateCell } from "src/components";
import { getTagTypeFromStatus } from "src/components/bid-contracts/BidContractHeader";
import { BidContractsTableRowFragment } from "src/components/bid-contracts/BidContractsTable.generated";
import { BidContractRevisionStatus } from "src/generated/graphqlTypes";
import {
  DevelopmentContractQuery,
  useDevelopmentContractQuery,
} from "src/routes/developments/procurement/DevelopmentContractsTable.generated";
import { createDevelopmentContractOverviewUrl } from "src/RouteUrls";

type DevelopmentContractsTableProps = { developmentId: string };

export function DevelopmentContractsTable(props: DevelopmentContractsTableProps) {
  const { developmentId } = props;
  const query = useDevelopmentContractQuery({ variables: { developmentId } });
  const layoutState = useGridTableLayoutState<DevelopmentContractsFilter>({
    persistedFilter: { filterDefs, storageKey: "developmentContractsFilter" },
    search: "client",
  });

  const { filter } = layoutState;
  const createRows = useCallback(
    (data: DevelopmentContractQuery | undefined) => createDevelopmentContractsRows(data, filter),
    [filter],
  );
  const columns = useMemo(() => createDevelopmentContractsColumns(), []);
  const rowStyles = useMemo(() => createDevelopmentContractsRowStyles(developmentId), [developmentId]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      hideEditColumns
      tableProps={{
        query,
        createRows,
        columns,
        rowStyles,
        sorting: { on: "client" },
        emptyFallback: "There are no contracts for this development yet.",
      }}
    />
  );
}

type DevelopmentContractsFilter = Partial<{
  primary: ("primary" | "secondary")[];
  status: BidContractRevisionStatus[];
  needsRevision: boolean;
}>;

type Row = SimpleHeaderAndData<BidContractsTableRowFragment>;

const filterDefs: FilterDefs<DevelopmentContractsFilter> = {
  primary: multiFilter({
    options: [
      { value: "primary" as const, label: "Primary" },
      { value: "secondary" as const, label: "Secondary" },
    ],
    getOptionLabel: (tp) => tp.label,
    getOptionValue: (tp) => tp.value,
    label: "Preference",
  }),
  status: multiFilter({
    options: Object.values(BidContractRevisionStatus).map((cs) => ({ label: cs, value: cs })),
    getOptionLabel: (cs) => capitalCase(cs.label),
    getOptionValue: (cs) => cs.value,
    label: "Status",
  }),
  needsRevision: checkboxFilter({ label: "Needs Revision" }),
};

function createDevelopmentContractsRows(
  data: DevelopmentContractQuery | undefined,
  filter: DevelopmentContractsFilter,
): GridDataRow<Row>[] {
  const { primary, status, needsRevision } = filter;
  return simpleDataRows(
    (data?.development.bidContracts ?? [])
      // Remove the internal estimate bid contract from the table results
      .filter((bc) => !bc.isInternalEstimate)
      .filter((bc) => !primary || primary.includes(bc.isPrimary ? "primary" : "secondary"))
      .filter((bc) => !status || status.includes(bc.latestRevision.status.code))
      .filter((bc) => !needsRevision || bc.latestRevision.hasOutdatedScope),
  );
}

function createDevelopmentContractsRowStyles(developmentId: string): RowStyles<Row> {
  // A bid contract will always be redirected to its latest revision
  return {
    data: { rowLink: (row) => createDevelopmentContractOverviewUrl(developmentId, row.data.latestRevision.id) },
  };
}

function createDevelopmentContractsColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      header: emptyCell,
      data: (data) => ({
        content: data.latestRevision.hasOutdatedScope && (
          <Tooltip title="This contract contains updated line items">
            <Icon icon="errorCircle" />
          </Tooltip>
        ),
        alignment: "center",
        css: Css.px0.$,
      }),
      clientSideSort: false,
      w: "32px",
    }),
    column<Row>({ header: "Trade Partner", data: (data) => data.tradePartner?.name, w: 2, mw: "240px" }),
    column<Row>({ header: "Preference", data: (data) => (data.isPrimary ? "Primary" : "Secondary"), mw: "120px" }),
    column<Row>({
      header: "Version",
      data: (data) => ({ content: data.latestRevision.version, value: parseInt(data.latestRevision.version || "0") }),
      mw: "100px",
    }),
    column<Row>({
      header: "Cost Codes",
      data: (data) => chipCell(data.latestRevision.costCodes.map((cc) => cc.number)),
      w: 2,
      mw: "200px",
    }),
    dateColumn<Row>({ header: "Effective On", data: (data) => dateCell(data.latestRevision.startDate), mw: "120px" }),
    dateColumn<Row>({ header: "Expires On", data: (data) => dateCell(data.latestRevision.endDate), mw: "120px" }),
    column<Row>({
      header: "Status",
      data: (data) => ({
        content: (
          <Tag
            text={data.latestRevision.status?.name || "Draft"}
            type={getTagTypeFromStatus(data.latestRevision.status?.code)}
          />
        ),
        value: data.latestRevision.status?.code,
      }),
      mw: "120px",
    }),
  ];
}
